// v2 gas station — a roadside drive-through forecourt: a wide flat canopy wrapped
// in a red brand fascia rides on two side pylons so a truck rolls straight
// through underneath, a raised twin-pump island runs down the middle (two red
// pumps back to back with cream dials, hoses holstered either side), a roadside
// price totem stands at the front corner and the horizontal fuel store tank rests
// on saddles across the back. Ported 1:1 from age-of-mcp's v2/gas_station.ts,
// using the library's shared `foundationSlab` and `scaffold` helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, type Part } from "../../lib";

const CONCRETE = "#9a9a96";  // pump island
const SADDLE   = "#6a6a66";  // tank saddles
const PUMP     = "#c43b2f";  // red pump bodies + brand fascia
const PANEL    = "#ece8da";  // dials / canopy deck / totem board
const HOSE     = "#26211b";  // hoses, nozzles, filler cap
const TANK     = "#7a8288";  // fuel store tank
const PYLON    = "#8a9096";  // canopy pylons + totem pole
const AMBER    = "#e8a830";  // totem price stripe
const DARKP    = "#3a4148";  // totem price stripe

const BASE = 0.14;           // foundation slab top

// The horizontal fuel store tank on its two saddles across the back, filler
// cap on top.
function storeTank(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.07, 0.12, 0.20), color: SADDLE,
      position: [-0.26, BASE + 0.06, -0.30] },
    { geometry: new THREE.BoxGeometry(0.07, 0.12, 0.20), color: SADDLE,
      position: [0.02, BASE + 0.06, -0.30] },
    { geometry: new THREE.CylinderGeometry(0.125, 0.125, 0.42, 8), color: TANK,
      position: [-0.12, BASE + 0.19, -0.30], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.CylinderGeometry(0.028, 0.028, 0.035, 6), color: HOSE,
      position: [-0.12, BASE + 0.33, -0.30] },
  ];
}

// The raised twin-pump island down the middle of the forecourt: two red
// pumps back to back, a cream dial each, a hose holstered on each flank.
function pumpIsland(): Part[] {
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(0.18, 0.07, 0.48), color: CONCRETE,
      position: [0, BASE + 0.035, -0.04] },
  ];
  for (const [pz, side] of [[-0.16, -1], [0.10, 1]] as const) {
    parts.push({ geometry: new THREE.BoxGeometry(0.13, 0.30, 0.11), color: PUMP,
      position: [0, BASE + 0.22, pz] });
    parts.push({ geometry: new THREE.BoxGeometry(0.10, 0.09, 0.02), color: PANEL,
      position: [0, BASE + 0.29, pz + side * 0.065] });
    parts.push({ geometry: new THREE.CylinderGeometry(0.018, 0.018, 0.16, 5),
      color: HOSE, position: [side * 0.075, BASE + 0.19, pz],
      rotation: [0, 0, side * 0.55] });
    parts.push({ geometry: new THREE.BoxGeometry(0.035, 0.07, 0.035), color: HOSE,
      position: [side * 0.115, BASE + 0.105, pz] });
  }
  return parts;
}

// The drive-through canopy: a flat deck on two side pylons, wrapped in a red
// brand fascia on all four edges.
function canopy(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.08, 0.58, 0.08), color: PYLON,
      position: [-0.30, BASE + 0.29, -0.04] },
    { geometry: new THREE.BoxGeometry(0.08, 0.58, 0.08), color: PYLON,
      position: [0.30, BASE + 0.29, -0.04] },
    { geometry: new THREE.BoxGeometry(0.78, 0.05, 0.50), color: PANEL,
      position: [0, 0.745, -0.04] },
    { geometry: new THREE.BoxGeometry(0.78, 0.08, 0.035), color: PUMP,
      position: [0, 0.745, 0.2275] },
    { geometry: new THREE.BoxGeometry(0.78, 0.08, 0.035), color: PUMP,
      position: [0, 0.745, -0.3075] },
    { geometry: new THREE.BoxGeometry(0.035, 0.08, 0.57), color: PUMP,
      position: [-0.4075, 0.745, -0.04] },
    { geometry: new THREE.BoxGeometry(0.035, 0.08, 0.57), color: PUMP,
      position: [0.4075, 0.745, -0.04] },
  ];
}

// The roadside price totem at the front corner: pole, board, three price
// stripes and a red brand cap.
function totem(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.05, 0.50, 0.05), color: PYLON,
      position: [0.35, BASE + 0.25, 0.34] },
    { geometry: new THREE.BoxGeometry(0.18, 0.24, 0.045), color: PANEL,
      position: [0.35, 0.71, 0.34] },
    { geometry: new THREE.BoxGeometry(0.14, 0.045, 0.012), color: PUMP,
      position: [0.35, 0.78, 0.369] },
    { geometry: new THREE.BoxGeometry(0.14, 0.045, 0.012), color: AMBER,
      position: [0.35, 0.715, 0.369] },
    { geometry: new THREE.BoxGeometry(0.14, 0.045, 0.012), color: DARKP,
      position: [0.35, 0.65, 0.369] },
    { geometry: new THREE.BoxGeometry(0.10, 0.035, 0.05), color: PUMP,
      position: [0.35, 0.8475, 0.34] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) return [foundationSlab(0.84), ...storeTank(), ...scaffold(0.82, 0.5)];
  if (s === 2) {
    return [foundationSlab(0.84), ...storeTank(), ...pumpIsland(), ...scaffold(0.82, 0.8)];
  }
  return [
    foundationSlab(0.84),
    ...storeTank(),
    ...pumpIsland(),
    ...canopy(),
    ...totem(),
  ];
}

export const gasStationV2 = defineModel({
  id: "gas-station-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Gas station (v2)",
  tags: ["building","gas","station","low-poly","age-of-mcp","fable"],
  summary: "Low-poly gas station.",
  description: "A fuel pump where vehicles refuel, holding a stock of fuel.",
  author: "age-of-mcp",
  footprint: 0.42,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
